import type {
  Batch,
  BatchAssignment,
  BatchCommand,
  BatchCommandAccepted,
  CreateBatchBody,
  PreflightResult,
  RecoverDecision,
} from '@ctcells/contract'
import { REQUEST_TIMEOUT_MS } from '@ctcells/core'

import { orchestrator } from '../clients.ts'
import { createCommand, newCommandId } from '../command.ts'
import { keys } from '../keys.ts'

/**
 * 배치 명령 5종 + 생성 + Pre-flight.
 *
 * 🔴 **버튼 활성은 `Batch.allowed_commands` 가 판정한다.** 여기 훅이 존재한다는 사실이
 *    버튼을 켜는 근거가 아니다. 화면은 `commandGate()` 로만 켠다.
 * 🔴 응답은 **202 Accepted** 다 — 결과는 스트림으로 온다. 낙관적으로 쓸 값이 응답에 없다.
 * ⚠ 실물 서버에 **쏴 보지 않았다**(쓰기 금지). 요청 모양은 계약 기준이다.
 */

interface BatchCommandVars {
  batchId: string
  /** 재시도 시 **같은 값을 다시 넘긴다.** 새로 만들면 멱등이 깨진다 */
  commandId?: string
}

/** 시작 변수 — 확약할 경고 코드가 더 붙는다 */
export interface StartBatchVars extends BatchCommandVars {
  /**
   * 그 시점 Pre-flight 가 낸 경고의 **코드**. 화면이 사람에게 보이고 확약받은 것 그대로다.
   * 비었거나 `undefined` 면 확약 키를 **싣지 않는다**(= 경고가 없는 배치).
   */
  acknowledgedWarnings?: readonly string[]
}

/**
 * 확약 두 칸을 만든다. 🔴 **중복을 제거하고 정렬한다** — 서버가 집합으로 대조하기 때문이고,
 * 경고 항목은 같은 코드가 여러 자리에서 난다(실측: `REAGENT_VOLUME_LOW` × 8).
 */
function startWarnAck(
  codes: readonly string[] | undefined,
): { warn_ack: true; acknowledged_warnings: string[] } | Record<string, never> {
  if (codes === undefined || codes.length === 0) return {}
  return { warn_ack: true, acknowledged_warnings: [...new Set(codes)].sort() }
}

function batchCommand(command: Extract<BatchCommand, 'pause'>) {
  return createCommand<BatchCommandVars, BatchCommandAccepted>({
    send: ({ batchId, commandId }) =>
      orchestrator.request<BatchCommandAccepted>({
        method: 'POST',
        path: `/batches/${batchId}/${command}`,
        body: { command_id: commandId ?? newCommandId() },
        timeoutMs: REQUEST_TIMEOUT_MS.command,
        retryOn401: true,
      }),
    invalidates: ({ batchId }) => [keys.batches.detail(batchId), keys.batches.all()],
  })
}

/**
 * 시작.
 *
 * 🔴 **경고 확약을 실어야 한다 — 2026-09-22 에 되살렸다.** 2026-09-20 에 「서버가 받지
 *    않는다」로 걷었는데 **그 관찰이 낡았다.** 지금 실서버는 `StartBatchEnvelope` 를 받고
 *    (`warn_ack` · `acknowledged_warnings`) `ctcells-core@6683b7a` 가 그 게이트를 **집행**한다.
 *    ⇒ Pre-flight 경고가 1건이라도 있으면 **`command_id` 만 보내는 시작은 409 로 떨어진다**
 *      (`BATCH_COMMAND_NOT_ALLOWED` · `context.reason = warn_ack_required`).
 *      실측 2026-09-22: 완료 배치의 Pre-flight 가 `REAGENT_VOLUME_LOW` 8건을 냈다 —
 *      **지금 로컬의 모든 배치가 이 자리에 걸린다.**
 * 🔴 **`acknowledged_warnings` 는 «코드 집합»이다** — 서버가
 *    `sorted({w.code for w in warnings})` 와 대조한다. 항목이 8건이어도 코드가 한 종이면
 *    한 개를 보낸다. 화면이 목록을 그대로 실으면 `warn_ack_mismatch` 다.
 * 🔴 **경고가 없으면 키를 «싣지 않는다».** 빈 배열을 보내면 확약 스냅샷이 「아무것도 안 봤다」로
 *    원장에 남는다 — 「무엇을 알고 시작했나」가 그 칸의 존재 이유다.
 * ⚠ 시작은 서버가 **같은 트랜잭션에서 Pre-flight 를 재평가**한다(`SM-BATCH-07`) —
 *   화면이 미리 통과시켰다고 시작이 보장되지 않는다. 확약 목록도 **그 시점** 값과 대조된다.
 */
export const useStartBatch = createCommand<StartBatchVars, BatchCommandAccepted>({
  send: ({ batchId, commandId, acknowledgedWarnings }) =>
    orchestrator.request<BatchCommandAccepted>({
      method: 'POST',
      path: `/batches/${batchId}/start`,
      body: {
        command_id: commandId ?? newCommandId(),
        ...startWarnAck(acknowledgedWarnings),
      },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: ({ batchId }) => [keys.batches.detail(batchId), keys.batches.all()],
})

/**
 * 중단(종착). 🔴 **되돌릴 수 없는 명령이라 사유가 필수다**(`IrreversibleCommandEnvelope`) —
 * `audit_log.detail.reason` 으로 해시체인에 봉인된다. 화면이 사유를 받아 넘겨야 한다.
 */
export const useStopBatch = createCommand<
  BatchCommandVars & { reason: string },
  BatchCommandAccepted
>({
  send: ({ batchId, commandId, reason }) =>
    orchestrator.request<BatchCommandAccepted>({
      method: 'POST',
      path: `/batches/${batchId}/stop`,
      body: { command_id: commandId ?? newCommandId(), reason },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: ({ batchId }) => [keys.batches.detail(batchId), keys.batches.all()],
})

export const usePauseBatch = batchCommand('pause')

/**
 * 끊긴 시간 원자(원심 회전 · 진탕)를 다시 돌릴 방식(계약 `ResumeBatchEnvelope.timed_atom` · `RecoverDecision.timed_atom`).
 * `restart` = 처음부터(생략과 같다) · `remaining` = 남은 시간만. 🔴 `remaining` 은 `Batch.resume_timed_atom_choices` 에 있을 때만
 * 서버가 받는다 — 화면은 그 칸이 둘일 때만 고르기를 세운다(eric 2026-10-22 · 백엔드 0.28.15 반영).
 */
export type TimedAtom = NonNullable<RecoverDecision['timed_atom']>

/** 재개 — 고른 방식이 있을 때만 `timed_atom` 을 싣는다(안 고르면 키가 없다 = 처음부터) */
export const useResumeBatch = createCommand<
  BatchCommandVars & { timedAtom?: TimedAtom },
  BatchCommandAccepted
>({
  send: ({ batchId, commandId, timedAtom }) =>
    orchestrator.request<BatchCommandAccepted>({
      method: 'POST',
      path: `/batches/${batchId}/resume`,
      body: {
        command_id: commandId ?? newCommandId(),
        ...(timedAtom === undefined ? {} : { timed_atom: timedAtom }),
      },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: ({ batchId }) => [keys.batches.detail(batchId), keys.batches.all()],
})

/** 복구 판정 변수 — 확인 재개(`rerun_cut_atom`)는 현장 확인 두 칸과 시간 원자 방식이 더 붙는다 */
export interface RecoverBatchVars extends BatchCommandVars {
  decision: RecoverDecision['decision']
  reason?: string
  /** `rerun_cut_atom` 에서 참이어야 한다(아니면 422 `physical_check_required`) — 사람이 현장을 확인했다 */
  physicalChecked?: boolean
  /** `rerun_cut_atom` 에서 끊김이 따로 요구하는 확인(`Batch.resume_checks`) — 사람이 하나씩 체크한 것 */
  physicalChecks?: readonly NonNullable<RecoverDecision['physical_checks']>[number][]
  timedAtom?: TimedAtom
  /**
   * `discard_samples` · `rerun_samples` 의 시료(계약 `RecoverDecision.sample_ids` · 최소 1건). 받지 못한 시료는 422 `context.sample_ids` —
   * 그 요청은 아무것도 바꾸지 않는다. 다른 결정에서는 싣지 않는다(`DispositionModal`).
   */
  sampleIds?: readonly string[]
}

/**
 * 복구 판정. `discard`·`confirm_completed` 는 사유가 필수다(감사 기록).
 * 🔴 확인 재개(`decision=rerun_cut_atom`)는 `physical_checked: true` 가 필수다 — `Batch.resume_route=confirm` 일 때 [재개]
 *    대신 이 길로만 잇는다(서버 `cut_confirm_required`). 안 실은 칸은 키째 뺀다.
 */
export const useRecoverBatch = createCommand<RecoverBatchVars, BatchCommandAccepted>({
  send: ({
    batchId,
    commandId,
    decision,
    reason,
    physicalChecked,
    physicalChecks,
    timedAtom,
    sampleIds,
  }) =>
    orchestrator.request<BatchCommandAccepted>({
      method: 'POST',
      path: `/batches/${batchId}/recover`,
      body: {
        command_id: commandId ?? newCommandId(),
        decision,
        ...(reason === undefined ? {} : { reason }),
        ...(physicalChecked === undefined ? {} : { physical_checked: physicalChecked }),
        ...(physicalChecks === undefined || physicalChecks.length === 0
          ? {}
          : { physical_checks: [...physicalChecks] }),
        ...(timedAtom === undefined ? {} : { timed_atom: timedAtom }),
        ...(sampleIds === undefined || sampleIds.length === 0
          ? {}
          : { sample_ids: [...sampleIds] }),
      },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  /* 🔴 시료 처분(`discard_samples` · `rerun_samples`)은 시료 상태 · 배정을 바꾼다 — 시료도 다시 받는다 */
  invalidates: ({ batchId }) => [
    keys.batches.detail(batchId),
    keys.batches.all(),
    keys.samples.all(),
    keys.history.all(),
  ],
})

/**
 * Pre-flight. ⚠ **본문이 없다** — 계약에 `requestBody` 가 선언돼 있지 않다(기록을 바꾸지 않는 쓰기).
 * ⚠ **제어락이 필요 없다**(실측 2026-09-22 — `views/batches.py` 의 이 라우터만
 *   `require_control_lock` 의존이 없다). 시작·중지와 달리 락을 안 쥔 사람도 점검할 수 있다.
 * 🔴 응답의 `warnings` 는 **막지 않지만 시작에 확약을 요구한다** — `useStartBatch` 로 그
 *    코드 집합을 넘겨야 한다. 여기서 세지 않고 화면이 사람에게 보인 것을 넘긴다.
 */
export const usePreflightBatch = createCommand<{ batchId: string }, PreflightResult>({
  send: ({ batchId }) =>
    orchestrator.request<PreflightResult>({
      method: 'POST',
      path: `/batches/${batchId}/preflight`,
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: ({ batchId }) => [keys.batches.detail(batchId)],
})

export interface CreateBatchVars {
  recipeId: string
  sampleIds: readonly string[]
  /**
   * 시료 ↔ 웰 배정. 🔴 **선택이다 — 안 주면 키 자체를 싣지 않는다**(배정 없이 뜬다).
   * 🔴 주면 **전건**이어야 한다(부분은 422). 전건인지 가르는 곳은 화면의 `assignmentsBody()` 이고,
   *    여기서는 받은 것을 그대로 싣기만 한다 — 빈 배열도 «안 준 것»으로 접어 키를 뺀다
   *    (서버도 `if assignments:` 로 빈 배열을 없는 것으로 읽는다 · `views/batches.py`).
   */
  assignments?: readonly BatchAssignment[] | undefined
  /**
   * 사람이 적은 작업 이름. 🔴 **비었거나 `undefined` 면 키 자체를 싣지 않는다** — 서버가
   * 「레시피 · 수행인 · 시각」으로 조립한다. 공백 걷기는 화면(`batchNameBody`)이 한다.
   * ⚠ **지금 서버는 받지 않는다**(`docs/backend-requests.csv` BR-94) — 422 없이 버리고 조립 이름을 저장한다.
   *   버려졌는지는 응답 `Batch.name` 으로 화면이 판정한다(`ignoredBatchName`).
   */
  name?: string | undefined
  commandId?: string
}

/**
 * 🔴 **93번 요청 · 서버가 받는 날 생성물(`CreateBatchBody`)에 `name` 이 들어오면 이 확장을 걷는다.**
 *    지금 생성물에는 `name` 이 없어 `satisfies CreateBatchBody` 가 그 키를 거절한다.
 */
type CreateBatchRequestBody = CreateBatchBody & { name?: string }

/** 🔴 `sample_ids` 는 **사전 등록된 시료**(`POST /samples`)의 id 다. 최대 8 + Ref 1, 최소 1건 */
export const useCreateBatch = createCommand<CreateBatchVars, Batch>({
  send: ({ recipeId, sampleIds, assignments, name, commandId }) =>
    orchestrator.request<Batch>({
      method: 'POST',
      path: '/batches',
      body: {
        command_id: commandId ?? newCommandId(),
        recipe_id: recipeId,
        sample_ids: [...sampleIds],
        ...(assignments === undefined || assignments.length === 0
          ? {}
          : {
              assignments: assignments.map(({ sample_id, wellName }) => ({ sample_id, wellName })),
            }),
        ...(name === undefined || name === '' ? {} : { name }),
      } satisfies CreateBatchRequestBody,
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    }),
  invalidates: () => [keys.batches.all(), keys.samples.all()],
})
